import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, KeyRound } from "lucide-react";
import { AuthShell, inputCls } from "@/components/AuthShell";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { useSEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  useSEO({ title: "Reset Password — Loan Salaah", description: "Choose a new password for your Loan Salaah account." });
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Password reset"
      title="Choose a new password."
      subtitle="Eight characters minimum. You'll sign in with it right after."
    >
      {!token ? (
        <p data-testid="reset-no-token" className="mt-8 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
          This reset link is incomplete. Request a fresh one from the{" "}
          <Link to="/forgot-password" className="font-semibold underline">forgot password</Link> page.
        </p>
      ) : done ? (
        <div data-testid="reset-success" className="mt-8 rounded-2xl border border-sage/40 bg-sage/10 p-6 text-center">
          <CheckCircle2 className="mx-auto h-8 w-8 text-concordia" />
          <p className="mt-4 text-sm font-semibold text-charcoal">Password updated</p>
          <Link to="/login" data-testid="reset-login-link"
            className="mt-5 inline-block rounded-full bg-concordia px-8 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-concordia-deep">
            Sign in
          </Link>
        </div>
      ) : (
        <form data-testid="reset-form" onSubmit={submit} className="mt-8 space-y-5" noValidate>
          <div>
            <label htmlFor="reset-password-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">New password</label>
            <input id="reset-password-input" data-testid="reset-password-input" type="password" className={inputCls} placeholder="Minimum 8 characters"
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p data-testid="reset-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{error}</p>}
          <button type="submit" data-testid="reset-submit-button" disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-concordia px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep disabled:cursor-not-allowed disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
